import {
  DailyCheckInCampaignType,
  DISCORD_CHECKIN_COMMAND
} from '@apeiron/reward'
import { INFO_POPUP_TYPE, InfoPopup } from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import CheckInByAnimaSection from '@src/components/reward/checkIn/CheckInByAnimaSection'
import CheckInMakeupStep from '@src/components/reward/checkIn/CheckInMakeupStep'
import ENV_CONFIG from '@src/config'
import { FeatureFlagType } from '@src/constants/featureFlag'
import { useDailyCheckInContext } from '@src/contexts/reward/DailyCheckInContext'
import featureAllowed from '@src/util/apeiron/featureAllowed'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const StyledInfoPopup = styled(InfoPopup)`
  max-height: 90%;
  max-width: 550px;
  width: 90%;
  .Custom-Content {
    margin-top: -20px;
  }
`

const TopSectionContainer = styled(Box)`
  padding: 0px 24px 14px 24px;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const BottomSectionContainer = styled(Box)`
  padding: 14px;
  background-color: #383838;
`

const Title = styled(Typography)`
  font-size: 24px;
  font-weight: 600;
  line-height: 30px;
  text-align: center;
`

const Subtitle = styled(Typography)`
  color: #838383;
  font-size: 12px;
  font-weight: 300;
`

const SectionTitle = styled(Typography)`
  align-self: flex-start;
  font-size: 16px;
`

const OrangeText = styled(Typography)`
  color: #ffbf36;
  display: inline;
  font-weight: 700;
`

const CheckInMakeupPopup: FC<Props> = () => {
  const {
    showMakeupPopup,
    myDailyCheckInCampaign,
    setShowMakeupPopup,
    getCompletedMakeupDaysCount,
    getTotalMakeupableDaysCount,
    fetchMyDailyCheckInCampaign,
    checkDiscordMakeUp
  } = useDailyCheckInContext()

  const { t } = useTranslation()

  useEffect(() => {
    if (showMakeupPopup) {
      fetchMyDailyCheckInCampaign({
        campaignTypes: [DailyCheckInCampaignType.WeeklyStardust]
      })
    }
  }, [fetchMyDailyCheckInCampaign, showMakeupPopup])

  const handleOnClose = useCallback(() => {
    setShowMakeupPopup(false)
  }, [setShowMakeupPopup])

  const campaign = myDailyCheckInCampaign[0]

  const makeupableDaysCount = useMemo((): number => {
    return getTotalMakeupableDaysCount(campaign)
  }, [campaign, getTotalMakeupableDaysCount])

  const completedMakeupDaysCount = useMemo((): number => {
    return getCompletedMakeupDaysCount(campaign)
  }, [campaign, getCompletedMakeupDaysCount])

  return (
    <StyledInfoPopup
      type={INFO_POPUP_TYPE.BLACK}
      open={showMakeupPopup}
      onClose={handleOnClose}
    >
      <TopSectionContainer>
        <Title>{t('reward.checkin.makeup.title')}</Title>
        <Subtitle>{t('reward.checkin.makeup.complete_mission')}</Subtitle>
        <SectionTitle>
          <Trans
            components={{
              orange: <OrangeText />
            }}
          >
            {t('reward.checkin.makeup.already_makeup', {
              count: completedMakeupDaysCount,
              total: makeupableDaysCount
            })}
          </Trans>
        </SectionTitle>
        <CheckInMakeupStep
          title={t('reward.checkin.makeup.check_discord')}
          description={t('reward.checkin.makeup.steps', {
            command: DISCORD_CHECKIN_COMMAND
          })}
          url={ENV_CONFIG.EXTERNAL_URL.DISCORD.CHECK_IN}
          madeUp={checkDiscordMakeUp(campaign)}
        />
      </TopSectionContainer>
      {featureAllowed(FeatureFlagType.ShowRewardMakeupCheckInByAnima) && (
        <BottomSectionContainer>
          <CheckInByAnimaSection />
        </BottomSectionContainer>
      )}
    </StyledInfoPopup>
  )
}

type Props = {}

export default CheckInMakeupPopup
